import { useCallback, useMemo, Dispatch, SetStateAction } from 'react';
import { create } from 'zustand';
import OVERLAYS from '@constants/overlays';
import { IOverlayContext } from './interfaces';

// NOTE (TASK-04, context→zustand): file name kept as *Provider for now; it no
// longer renders a React Context provider. Renaming is handled by TASK-08.

const EXCLUSIVE_OVERLAYS: string[] = [OVERLAYS.ADMIN_TOOLS, OVERLAYS.PROFILE];

interface IOverlayStore {
  currentlyOpenOverlays: string[];
  hideCurrentlyOpenOverlays: boolean;
  addToCurrentlyOpenOverlays: (overlayName: string) => void;
  removeFromCurrentlyOpenOverlays: (overlayName: string) => void;
  setHideCurrentlyOpenOverlays: Dispatch<SetStateAction<boolean>>;
  resetState: () => void;
}

const initialState: Pick<
  IOverlayStore,
  'currentlyOpenOverlays' | 'hideCurrentlyOpenOverlays'
> = {
  currentlyOpenOverlays: [],
  hideCurrentlyOpenOverlays: false,
};

export const useOverlayStore = create<IOverlayStore>((set) => ({
  ...initialState,
  addToCurrentlyOpenOverlays: (overlayName) =>
    set((state) => {
      if (state.currentlyOpenOverlays.includes(overlayName)) {
        return state;
      }

      const base = EXCLUSIVE_OVERLAYS.includes(overlayName)
        ? state.currentlyOpenOverlays.filter(
            (name) => !EXCLUSIVE_OVERLAYS.includes(name),
          )
        : state.currentlyOpenOverlays;

      return { currentlyOpenOverlays: [...base, overlayName] };
    }),
  removeFromCurrentlyOpenOverlays: (overlayName) =>
    set((state) => {
      if (!state.currentlyOpenOverlays.includes(overlayName)) {
        return state;
      }

      return {
        currentlyOpenOverlays: state.currentlyOpenOverlays.filter(
          (name) => name !== overlayName,
        ),
      };
    }),
  setHideCurrentlyOpenOverlays: (value) =>
    set((state) => ({
      hideCurrentlyOpenOverlays:
        typeof value === 'function'
          ? value(state.hideCurrentlyOpenOverlays)
          : value,
    })),
  resetState: () => set({ ...initialState }),
}));

// Backwards-compatible hook: consumers keep calling useOverlayContext() and get
// the same shape. isOverlayCurrentlyOpen is rebuilt whenever the open list
// changes (stable identity per list), preserving the effect dependencies in
// useOverlay() that the old context-derived callback relied on.
export const useOverlayContext = (): IOverlayContext => {
  const currentlyOpenOverlays = useOverlayStore(
    (state) => state.currentlyOpenOverlays,
  );
  const hideCurrentlyOpenOverlays = useOverlayStore(
    (state) => state.hideCurrentlyOpenOverlays,
  );
  const addToCurrentlyOpenOverlays = useOverlayStore(
    (state) => state.addToCurrentlyOpenOverlays,
  );
  const removeFromCurrentlyOpenOverlays = useOverlayStore(
    (state) => state.removeFromCurrentlyOpenOverlays,
  );
  const setHideCurrentlyOpenOverlays = useOverlayStore(
    (state) => state.setHideCurrentlyOpenOverlays,
  );

  const isOverlayCurrentlyOpen = useCallback(
    (overlayName: string) => currentlyOpenOverlays.includes(overlayName),
    [currentlyOpenOverlays],
  );

  // Memoize the returned object so its identity is stable between renders
  // (matching the old provider's useMemo'd context value). Consumers that
  // depend on the whole context object — e.g. EditorModeContext — rely on this.
  return useMemo(
    () => ({
      addToCurrentlyOpenOverlays,
      removeFromCurrentlyOpenOverlays,
      isOverlayCurrentlyOpen,
      hideCurrentlyOpenOverlays,
      setHideCurrentlyOpenOverlays,
    }),
    [
      addToCurrentlyOpenOverlays,
      removeFromCurrentlyOpenOverlays,
      isOverlayCurrentlyOpen,
      hideCurrentlyOpenOverlays,
      setHideCurrentlyOpenOverlays,
    ],
  );
};
